import { useCallback, useEffect, useState } from "react";
import { formatPesos } from "../money";
import { modules } from "../../../core/platform/registry/registry";
import type { BooksApi, ProfitBody } from "./api";

/**
 * Is the café making money? (T031, User Story 4, FR-134.)
 *
 * Every figure is derived from the till's own records; nothing on this screen
 * was typed in by anyone. Where a figure cannot be known — cost of goods with
 * stock switched off, a margin with nothing costed yet — it says so instead of
 * showing a zero that would read as good news.
 */

const today = () => new Date().toISOString().slice(0, 10);

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function startOfMonth(): string {
  return `${new Date().toISOString().slice(0, 7)}-01`;
}

type PeriodId = "today" | "week" | "month" | "opening" | "custom";

const PERIODS: Array<{ id: PeriodId; label: string; from(): string }> = [
  { id: "today", label: "Today", from: today },
  { id: "week", label: "7 days", from: () => daysAgo(6) },
  { id: "month", label: "This month", from: startOfMonth },
  // The rollups only start on the first day of trading, so an early date is
  // simply "everything".
  { id: "opening", label: "Since opening", from: () => "2020-01-01" },
];

export interface ProfitProps {
  api: BooksApi;
  version: number;
}

export function Profit({ api, version }: ProfitProps) {
  const [period, setPeriod] = useState<PeriodId>("month");
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(today());
  const [body, setBody] = useState<ProfitBody | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBody(await api.profit(from, to));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not work out the profit");
    }
  }, [api, from, to]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const choose = (id: PeriodId) => {
    setPeriod(id);
    const chosen = PERIODS.find((entry) => entry.id === id);
    if (chosen) {
      setFrom(chosen.from());
      setTo(today());
    }
  };

  return (
    <section className="books__panel">
      <nav className="books__periods" aria-label="Period">
        {PERIODS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`books__period${period === entry.id ? " is-active" : ""}`}
            onClick={() => choose(entry.id)}
          >
            {entry.label}
          </button>
        ))}
        <button
          type="button"
          className={`books__period${period === "custom" ? " is-active" : ""}`}
          onClick={() => setPeriod("custom")}
        >
          Dates
        </button>
      </nav>

      {period === "custom" && (
        <div className="books__pickers">
          <label className="books__field">
            <span>From</span>
            <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label className="books__field">
            <span>To</span>
            <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </label>
        </div>
      )}

      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}

      {!body ? (
        <p className="books__hint">Working it out…</p>
      ) : body.tradingDays === 0 ? (
        // FR-115's rule, applied here: no trading must never look like a real zero.
        <p className="books__hint">No trading in these dates yet.</p>
      ) : (
        <>
          <p className="books__headline">
            <span>Operating profit</span>
            <strong className={body.operatingProfit < 0 ? "is-loss" : undefined}>
              ₱{formatPesos(body.operatingProfit)}
            </strong>
            <span className="books__row-meta">
              over {body.tradingDays} trading day{body.tradingDays === 1 ? "" : "s"}
            </span>
          </p>

          <ul className="books__list">
            <li className="books__row">
              <span className="books__row-name">Sales</span>
              <span className="books__figure">₱{formatPesos(body.sales)}</span>
              {body.breakEven.actualPerTradingDay !== null && (
                <span className="books__row-meta">
                  ₱{formatPesos(body.breakEven.actualPerTradingDay)} on an average trading day
                </span>
              )}
            </li>

            {/* What an extension gave away, apart from staff discounts. Sales
                are already net of it; this says what it was (006 FR-037). */}
            {(body.grantedFree ?? []).map((granted) => (
              <li key={granted.module} className="books__row">
                <span className="books__row-name">
                  Given free · {modules.all().find((m) => m.id === granted.module)?.name ?? granted.module}
                </span>
                <span className="books__figure">₱{formatPesos(granted.amount)}</span>
                <span className="books__row-meta">
                  {granted.count} item{granted.count === 1 ? "" : "s"} at full price, not in sales
                </span>
              </li>
            ))}

            <li className="books__row">
              <span className="books__row-name">
                Cost of goods
                {!body.costOfGoods.available && <span className="books__chip">not available</span>}
              </span>
              <span className="books__figure">
                {body.costOfGoods.available ? `₱${formatPesos(body.costOfGoods.amount)}` : "—"}
              </span>
              <span className="books__row-meta">
                {body.costOfGoods.available ? (
                  <>
                    against ₱{formatPesos(body.costOfGoods.costedSales)} of costed sales
                    {body.costOfGoods.uncostedSales > 0 && (
                      <> · ₱{formatPesos(body.costOfGoods.uncostedSales)} sold with no recipe, not costed</>
                    )}
                    {body.costOfGoods.unpricedIngredients.length > 0 && (
                      <>
                        {" "}
                        · never priced:{" "}
                        {body.costOfGoods.unpricedIngredients.map((one) => one.name).join(", ")}
                      </>
                    )}
                  </>
                ) : (
                  "Stock is switched off, so what was drunk cannot be costed."
                )}
              </span>
            </li>

            <li className="books__row">
              <span className="books__row-name">
                Running costs
                {body.runningCosts.estimated && <span className="books__chip">estimate</span>}
              </span>
              <span className="books__figure">₱{formatPesos(body.runningCosts.total)}</span>
              <span className="books__row-meta">
                ₱{formatPesos(body.runningCosts.actual)} paid
                {body.runningCosts.accrued > 0 && (
                  <> · ₱{formatPesos(body.runningCosts.accrued)} owed but not billed yet</>
                )}
              </span>
            </li>
          </ul>

          <h3 className="books__subhead">Break-even</h3>
          <ul className="books__list">
            <li className="books__row">
              <span className="books__row-name">A month</span>
              <span className="books__figure">
                {body.breakEven.perMonth === null ? "—" : `₱${formatPesos(body.breakEven.perMonth)}`}
              </span>
              {body.breakEven.perMonth === null && (
                <span className="books__row-meta">
                  Not enough costed sales yet to know what each peso leaves behind.
                </span>
              )}
            </li>
            {body.breakEven.perTradingDay !== null && (
              <li className="books__row">
                <span className="books__row-name">A trading day</span>
                <span className="books__figure">₱{formatPesos(body.breakEven.perTradingDay)}</span>
                <span className="books__row-meta">
                  beside ₱{formatPesos(body.breakEven.actualPerTradingDay ?? 0)} actually taken
                  {body.breakEven.actualPerTradingDay !== null &&
                    (body.breakEven.actualPerTradingDay >= body.breakEven.perTradingDay
                      ? " — above it"
                      : " — below it")}
                </span>
              </li>
            )}
          </ul>

          <h3 className="books__subhead">Paying it back</h3>
          <ul className="books__list">
            <li className="books__row">
              <span className="books__row-name">Spent to open</span>
              <span className="books__figure">₱{formatPesos(body.setupSpend)}</span>
              <span className="books__row-meta">
                {body.payback.months === null
                  ? "At the recent pace the setup is not being paid back."
                  : `About ${body.payback.months} months to pay back, at the ${body.payback.basis ?? "recent"} pace.`}
              </span>
            </li>
          </ul>
        </>
      )}
    </section>
  );
}
